import React, { useState, useEffect } from 'react';
import { Event, InvitationType } from '../../_graphql/generated/types';
import { InvitationDesign } from '../../_types/invitation-design-v2';
import InvitationDesignerV2 from '../../_components/InvitationDesigner/InvitationDesignerV2';
import { invitationTypesApi } from '../../_lib/api-client';

interface InvitationDesignPageProps {
  event: Event;
  invitationType: InvitationType;
  onBack: () => void;
  onDesignSaved?: () => void; // Callback to refresh parent data
  onboardingDesign?: InvitationDesign | null;
  isEditingNewType?: boolean;
}

const InvitationDesignPage: React.FC<InvitationDesignPageProps> = ({
  event,
  invitationType,
  onBack,
  onDesignSaved,
  onboardingDesign,
  isEditingNewType = false,
}) => {
  const [savedDesign, setSavedDesign] = useState<InvitationDesign | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboardingMessage, setShowOnboardingMessage] = useState(false);

  // Load existing design from invitation type on mount
  useEffect(() => {
    setIsLoading(true);
    
    if (invitationType.designCustomizations) {
      try {
        // If designCustomizations contains a design, load it
        let existingDesign: InvitationDesign;
        
        // Handle case where designCustomizations might be a string (JSON)
        if (typeof invitationType.designCustomizations === 'string') {
          existingDesign = JSON.parse(invitationType.designCustomizations);
        } else {
          existingDesign = invitationType.designCustomizations as InvitationDesign;
        }
        
        setSavedDesign(existingDesign);
      } catch (error) {
        console.error('Failed to load existing design:', error);
        setSavedDesign(null);
      }
    } else {
      // If no existing design, check for onboarding design
      if (onboardingDesign) {
        setSavedDesign(onboardingDesign);
        setShowOnboardingMessage(true);
      } else {
        setSavedDesign(null);
      }
    }
    
    setIsLoading(false);
  }, [invitationType, onboardingDesign]);

  const handleSaveDesign = async (design: InvitationDesign, selectedInvitationType?: InvitationType) => {
    const typeToSave = selectedInvitationType || invitationType;
    
    try {
      // Save design to the invitation type via GraphQL
      await invitationTypesApi.saveDesign({
        invitationTypeId: typeToSave.id,
        customizations: design
      });

      // Update local state
      setSavedDesign(design);
      
      // Always go back to event screen after saving
      onBack();
      
      // Notify parent to refresh data
      if (onDesignSaved) {
        onDesignSaved();
      }
    } catch (error) {
      console.error('Failed to save design:', error);
      alert('Failed to save invitation design. Please try again.');
    }
  };

  const handleCancelDesign = () => {
    onBack();
  };

  // Show loading state while design is being loaded
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invitation design...</p>
        </div>
      </div>
    );
  }

  // Always show the designer - no intermediate screen
  return (
    <div className="relative h-full">
      {/* Onboarding success message */}
      {showOnboardingMessage && (
        <div className="fixed top-20 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in max-w-md">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium">Your design has been loaded!</p>
              <p className="text-sm text-green-100 mt-1">Make any adjustments and save when ready.</p>
            </div>
            <button
              onClick={() => setShowOnboardingMessage(false)}
              className="ml-4 text-white/80 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <InvitationDesignerV2
        event={event}
        initialDesign={savedDesign || undefined}
        selectedInvitationType={invitationType}
        onSave={handleSaveDesign}
        onCancel={handleCancelDesign}
        forceTemplateSelection={isEditingNewType && !savedDesign}
        isNewInvitationType={isEditingNewType}
      />
    </div>
  );
};

export default InvitationDesignPage;